import { useEffect, useRef, useState } from 'react';
import { Auth } from 'Shared/auth';
import {
   GoogleOAuthProvider,
   GoogleLogin,
   type CredentialResponse,
   type GoogleLoginProps,
} from '@react-oauth/google';
import { fromPhpLangId } from '@ifixit/i18n/php-langid';
import { useTranslations } from '@ifixit/i18n/hooks';
import GoogleLogo from 'Shared/icons/logos/google.svg';
import { RecentlyUsedBadge } from './RecentlyUsedBadge';
import { SSOProviderButton } from './SSOProviderButton';

// Google Identity Services documents a 400px maximum button width.
const googleButtonMaxWidthPx = 400;
const googleButtonResizeDebounceMs = 100;
type GoogleSignInButtonText = Extract<GoogleLoginProps['text'], 'signin_with' | 'signup_with'>;

export function GoogleSSOButton({
   loginHandler,
   setError,
   text,
   recentlyUsed,
}: {
   loginHandler: (response: any) => void;
   setError: (error: string) => void;
   text: GoogleSignInButtonText;
   recentlyUsed?: boolean;
}) {
   const t = useTranslations('Login');
   const googleLoginContainerRef = useRef<HTMLDivElement>(null);
   const [googleButtonWidthPx, setGoogleButtonWidthPx] = useState<number>();

   useEffect(() => {
      const googleLoginContainer = googleLoginContainerRef.current;
      if (!googleLoginContainer) {
         return;
      }

      const updateGoogleButtonWidth = () => {
         const widthPx = Math.min(googleLoginContainer.clientWidth, googleButtonMaxWidthPx);
         if (widthPx > 0) {
            setGoogleButtonWidthPx(widthPx);
         }
      };

      updateGoogleButtonWidth();
      let resizeDebounceTimerId: number | undefined;
      const resizeObserver = new ResizeObserver(() => {
         if (resizeDebounceTimerId !== undefined) {
            window.clearTimeout(resizeDebounceTimerId);
         }
         resizeDebounceTimerId = window.setTimeout(
            updateGoogleButtonWidth,
            googleButtonResizeDebounceMs
         );
      });
      resizeObserver.observe(googleLoginContainer);

      return () => {
         resizeObserver.disconnect();
         if (resizeDebounceTimerId !== undefined) {
            window.clearTimeout(resizeDebounceTimerId);
         }
      };
   }, []);

   const signinCallback = (credentialResponse: CredentialResponse) => {
      if (credentialResponse.credential) {
         (Auth as any).sendLogin({
            googleOAuthData: credentialResponse,
            ajax: true,
            handler: loginHandler,
         });
      } else {
         setError(App.loginMessages.OAuth2_ACCESS_TOKEN);
      }
   };

   const errorCallback = () => {
      setError(App.loginMessages.OAuth2_ERROR);
   };

   // Google draws its button in an iframe on its own domain, so a tap inside it
   // raises no click event here. The mobile app hands Google sign-in to the
   // phone by listening for a click on #googleLogin, and it cannot hear one it
   // never gets. Give the app an ordinary button of ours to listen to.
   if (App.isOnMobileApp) {
      return (
         <SSOProviderButton
            appId="googleLogin"
            Logo={GoogleLogo}
            logoViewBox="0 0 25 26"
            label={text === 'signup_with' ? t('signUpWithGoogle') : t('signInWithGoogle')}
            recentlyUsed={recentlyUsed}
         />
      );
   }

   return (
      <GoogleOAuthProvider clientId={App.googleOAuth2Clientid}>
         <div
            id="googleLogin"
            ref={googleLoginContainerRef}
            className="w-full"
            data-mobile-appid="googleLogin"
         >
            <GoogleLogin
               onSuccess={signinCallback}
               onError={errorCallback}
               containerProps={{ className: 'relative z-1' }}
               locale={fromPhpLangId(App.lang)}
               text={text}
               theme="outline"
               size="large"
               shape="rectangular"
               logo_alignment="center"
               width={googleButtonWidthPx?.toString()}
            />
            {recentlyUsed && <RecentlyUsedBadge />}
         </div>
      </GoogleOAuthProvider>
   );
}
